// Terms.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Terms.css";

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Términos y Condiciones | Wayo</title>
        <meta name="description" content="Términos y condiciones de uso de Wayo. Lee nuestras condiciones legales para usar la app de nutrición inteligente." />
        <meta name="keywords" content="términos y condiciones, política de uso, wayo, app nutrición" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://wayoai.app/terms" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Términos y Condiciones | Wayo" />
        <meta property="og:description" content="Lee nuestras condiciones legales de uso" />
        <meta property="og:url" content="https://wayoai.app/terms" />
        <meta property="og:type" content="website" />
      </Helmet>
      <main className="terms-page">
        <Header />
    <section id="terms" className="terms-content">
      <div className="terms-container">
        <h1 className="terms-title">Términos y Condiciones</h1>
        
        <div className="terms-text">
            <p className="last-updated"><strong>Última actualización:</strong> 2 de diciembre de 2025</p>
            
            <p>Por favor, lee atentamente estos Términos y Condiciones antes de utilizar la aplicación Wayo.</p>

            <h2>1. Interpretación y Definiciones</h2>
            
            <h3>1.1 Definiciones</h3>
            <p>A los efectos de estos Términos y Condiciones:</p>
            <ul>
              <li><strong>Aplicación</strong> significa la aplicación móvil denominada "Wayo", incluyendo todas sus funcionalidades, herramientas, contenido y actualizaciones.</li>
              <li><strong>Servicio</strong> significa, colectivamente, la Aplicación, el Sitio Web, el software asociado, el contenido, las herramientas impulsadas por inteligencia artificial y cualquier servicio ofrecido.</li>
              <li><strong>Titular</strong>, <strong>Nosotros</strong> o <strong>Nos</strong> se refiere a Facundo Ezequiel Solis, con domicilio en Calle Albareda, 77, 2D, Las Palmas de Gran Canaria, España.</li>
              <li><strong>Sitio Web</strong> se refiere a wayoai.app y cualquier subdominio asociado.</li>
              <li><strong>Usuario</strong>, <strong>Tú</strong> o <strong>Te</strong> se refiere a la persona física que accede o utiliza el Servicio.</li>
              <li><strong>Contenido del Usuario</strong> significa cualquier imagen, fotografía, texto, metadatos u otro contenido que Tú cargues, envíes o transmitas a través del Servicio.</li>
              <li><strong>Dispositivo</strong> significa cualquier dispositivo capaz de acceder al Servicio.</li>
            </ul>

            <h2>2. Aceptación de los Términos</h2>
            <p>Tu acceso y uso del Servicio está condicionado a Tu aceptación de estos Términos y Condiciones y de nuestra Política de Privacidad. Al acceder o utilizar el Servicio, reconoces que has leído, comprendido y aceptado estar sujeto a estos Términos.</p>
            <p>Si no estás de acuerdo con alguna parte de estos Términos, debes dejar de utilizar el Servicio inmediatamente.</p>

            <h2>3. Requisitos de Edad y Elegibilidad</h2>
            <p>Debes tener al menos 14 años de edad para utilizar el Servicio de forma independiente.</p>
            <p>Si tienes entre 14 y 17 años, solo puedes utilizar el Servicio con el consentimiento y supervisión de un padre, madre o tutor legal, quien acepta ser plenamente responsable de todas las actividades realizadas en el Servicio.</p>
            <p>Los menores de 14 años no pueden utilizar el Servicio bajo ninguna circunstancia.</p>

            <h2>4. Descripción del Servicio</h2>
            <p>Wayo es una aplicación móvil de nutrición y fitness que utiliza inteligencia artificial para:</p>
            <ul>
              <li>Escanear y reconocer alimentos a partir de fotografías</li>
              <li>Proporcionar estimaciones de calorías y valores nutricionales</li>
              <li>Ofrecer seguimiento de ingesta calórica y nutricional</li>
              <li>Proporcionar recomendaciones personalizadas de fitness y nutrición</li>
              <li>Gestionar objetivos de salud y bienestar</li>
            </ul>

            <h2>5. Avisos Importantes de Salud, Nutrición y Seguridad</h2>
            
            <div className="warning-box">
              <h3>⚠️ INFORMACIÓN CRÍTICA - LEE ATENTAMENTE</h3>
              
              <p><strong>Wayo NO proporciona asesoramiento médico, consejo nutricional profesional ni servicios de salud.</strong></p>
              
              <p>Todas las estimaciones de calorías, información nutricional e identificación de alimentos son aproximaciones automatizadas generadas por inteligencia artificial y pueden ser incompletas, inexactas o incorrectas.</p>
              
              <p><strong>Limitaciones específicas:</strong></p>
              <ul>
                <li>Las estimaciones calóricas y nutricionales son aproximaciones y NO deben considerarse exactas</li>
                <li>La identificación de alimentos por IA puede contener errores significativos</li>
                <li>El Servicio NO está diseñado para diagnosticar, tratar, curar o prevenir ninguna enfermedad</li>
                <li>El Servicio NO sustituye el consejo médico, diagnóstico o tratamiento profesional</li>
                <li>El Servicio NO es un producto sanitario ni está regulado como tal</li>
              </ul>
              
              <p><strong>Debes consultar siempre con profesionales sanitarios cualificados:</strong></p>
              <ul>
                <li>Antes de iniciar cualquier dieta o programa de ejercicio</li>
                <li>Si tienes condiciones médicas preexistentes</li>
                <li>Si estás tomando medicación</li>
                <li>Si estás embarazada o en período de lactancia</li>
                <li>Si experimentas síntomas o efectos adversos</li>
              </ul>
              
              <p><strong>El Titular no se hace responsable de:</strong></p>
              <ul>
                <li>Inexactitudes en los conteos de calorías o valores nutricionales</li>
                <li>Errores en la identificación de alimentos</li>
                <li>Decisiones tomadas basándose en la información proporcionada por el Servicio</li>
                <li>Cualquier efecto adverso en tu salud derivado del uso del Servicio</li>
              </ul>
            </div>

            <h2>6. Contenido del Usuario y Licencia de Uso</h2>
            
            <h3>6.1 Tu Contenido</h3>
            <p>Al enviar Contenido del Usuario, declaras y garantizas que:</p>
            <ul>
              <li>Eres propietario o tienes los derechos necesarios sobre dicho contenido</li>
              <li>El contenido no infringe derechos de terceros</li>
              <li>El contenido no es ilegal, ofensivo, difamatorio ni perjudicial</li>
            </ul>

            <h3>6.2 Licencia que Nos Otorgas</h3>
            <p>Al enviar Contenido del Usuario, otorgas al Titular una licencia mundial, irrevocable, libre de regalías, transferible y sublicenciable para:</p>
            <ul>
              <li>Usar, reproducir, analizar y procesar el Contenido del Usuario</li>
              <li>Modificar y crear obras derivadas</li>
              <li>Entrenar y mejorar modelos de inteligencia artificial</li>
              <li>Operar, mantener y mejorar el Servicio</li>
            </ul>
            <p>Esta licencia se otorga con el propósito de operar el Servicio y mejorar la precisión de nuestras tecnologías de reconocimiento de alimentos mediante inteligencia artificial.</p>

            <h3>6.3 Eliminación de Contenido</h3>
            <p>Nos reservamos el derecho de eliminar cualquier Contenido del Usuario que, a nuestra discreción, viole estos Términos o sea inapropiado.</p>

            <h2>7. Usos Prohibidos</h2>
            <p>Al utilizar el Servicio, aceptas NO:</p>
            <ul>
              <li>Cargar contenido violento, explícito, pornográfico o ilegal</li>
              <li>Cargar imágenes de personas sin su consentimiento legal</li>
              <li>Realizar ingeniería inversa, descompilar o intentar extraer el código fuente del Servicio</li>
              <li>Utilizar el Servicio para análisis competitivo o desarrollo de productos similares</li>
              <li>Eludir medidas de seguridad o intentar acceso no autorizado</li>
              <li>Interferir con el funcionamiento del Servicio o servidores</li>
              <li>Usar el Servicio de manera que pueda dañar, deshabilitar o sobrecargar la infraestructura</li>
              <li>Recopilar datos de otros usuarios sin autorización</li>
              <li>Suplantar identidad o falsificar tu relación con cualquier persona o entidad</li>
              <li>Transmitir virus, malware o código malicioso</li>
            </ul>

            <h2>8. Suscripciones, Facturación y Compras</h2>
            
            <h3>8.1 Procesamiento de Pagos</h3>
            <p>Las compras y suscripciones se procesan a través de plataformas de terceros como Apple App Store y Google Play Store. Sus términos y condiciones rigen el procesamiento de pagos, renovaciones y reembolsos.</p>

            <h3>8.2 Renovación Automática</h3>
            <p>Las suscripciones se renuevan automáticamente a menos que las canceles al menos 24 horas antes de que finalice el período actual.</p>

            <h3>8.3 Derecho de Desistimiento (Consumidores UE/España)</h3>
            <p>De conformidad con la legislación de protección de consumidores de la Unión Europea y España, tienes derecho a desistir de la compra en un plazo de 14 días desde la contratación.</p>
            <p><strong>Importante:</strong> Si solicitas expresamente comenzar a utilizar el Servicio de suscripción dentro del período de desistimiento, reconoces que pierdes el derecho de desistimiento una vez que el servicio se haya prestado completamente.</p>
            <p>Para ejercer tu derecho de desistimiento, debes contactarnos en: soporte@wayoai.app</p>

            <h3>8.4 Modificaciones de Precios</h3>
            <p>Nos reservamos el derecho de modificar los precios de suscripción. Los cambios entrarán en vigor en el siguiente período de facturación tras notificarte con antelación razonable.</p>

            <h2>9. Servicios de Terceros</h2>
            <p>El Servicio puede incluir enlaces o integraciones con servicios, contenido o sitios web de terceros. No controlamos ni nos hacemos responsables de dichos servicios externos.</p>
            <p>Tu interacción con servicios de terceros se rige por sus propios términos y políticas.</p>

            <h2>10. Propiedad Intelectual</h2>
            <p>Todos los derechos, títulos e intereses sobre el Servicio (excluyendo el Contenido del Usuario) son propiedad exclusiva del Titular o de sus licenciantes.</p>
            <p>Esto incluye, sin limitación:</p>
            <ul>
              <li>El código fuente y software de la Aplicación</li>
              <li>Los algoritmos de inteligencia artificial</li>
              <li>El diseño, interfaz gráfica y elementos visuales</li>
              <li>Las marcas, logos y nombres comerciales</li>
              <li>La base de datos nutricional</li>
              <li>Cualquier contenido, documentación y materiales</li>
            </ul>
            <p>No puedes copiar, modificar, distribuir, vender, alquilar, realizar ingeniería inversa ni crear obras derivadas de ninguna parte del Servicio sin autorización expresa por escrito.</p>

            <h2>11. Protección de Datos y Privacidad</h2>
            <p>El tratamiento de tus datos personales se rige por nuestra Política de Privacidad, que cumple con el Reglamento General de Protección de Datos (RGPD) de la Unión Europea y la Ley Orgánica de Protección de Datos (LOPD) española.</p>
            
            <h3>11.1 Tus Derechos</h3>
            <p>Como usuario, tienes derecho a:</p>
            <ul>
              <li><strong>Acceso:</strong> Conocer qué datos personales tenemos sobre ti</li>
              <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
              <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos ("derecho al olvido")</li>
              <li><strong>Portabilidad:</strong> Recibir tus datos en formato estructurado</li>
              <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos</li>
              <li><strong>Limitación:</strong> Restringir el tratamiento de tus datos</li>
            </ul>
            <p>Para ejercer estos derechos, contacta: soporte@wayoai.app</p>

            <h3>11.2 Base Legal del Tratamiento</h3>
            <p>Tratamos tus datos en base a:</p>
            <ul>
              <li>Tu consentimiento explícito</li>
              <li>La ejecución del contrato (estos Términos)</li>
              <li>Nuestros intereses legítimos en mejorar el Servicio</li>
            </ul>

            <h2>12. Suspensión y Terminación</h2>
            
            <h3>12.1 Por Nuestra Parte</h3>
            <p>Podemos suspender o terminar tu acceso al Servicio inmediatamente si:</p>
            <ul>
              <li>Violas estos Términos</li>
              <li>Usas el Servicio de manera fraudulenta o ilegal</li>
              <li>Tu conducta puede dañar al Titular, otros usuarios o terceros</li>
            </ul>

            <h3>12.2 Por Tu Parte</h3>
            <p>Puedes cancelar tu cuenta en cualquier momento desde la configuración de la Aplicación o contactándonos en: soporte@wayoai.app</p>

            <h3>12.3 Efectos de la Terminación</h3>
            <p>Tras la terminación:</p>
            <ul>
              <li>Todos los derechos otorgados bajo estos Términos cesan inmediatamente</li>
              <li>Debes dejar de usar el Servicio</li>
              <li>Podemos eliminar tu cuenta y Contenido del Usuario</li>
              <li>Las disposiciones que por su naturaleza deban sobrevivir, permanecerán vigentes</li>
            </ul>

            <h2>13. Exención de Garantías</h2>
            <p>El Servicio se proporciona "TAL CUAL" y "SEGÚN DISPONIBILIDAD", sin garantías de ningún tipo, expresas o implícitas.</p>
            <p>En la máxima medida permitida por la ley, el Titular rechaza todas las garantías, incluyendo:</p>
            <ul>
              <li>Garantías de comerciabilidad</li>
              <li>Garantías de idoneidad para un propósito particular</li>
              <li>Garantías de precisión o fiabilidad</li>
              <li>Garantías de no infracción</li>
              <li>Garantías de funcionamiento ininterrumpido o libre de errores</li>
            </ul>
            <p>No garantizamos que:</p>
            <ul>
              <li>El Servicio cumpla tus requisitos específicos</li>
              <li>Los resultados obtenidos sean precisos o confiables</li>
              <li>Los errores serán corregidos</li>
              <li>El Servicio esté disponible en todo momento</li>
            </ul>

            <h2>14. Limitación de Responsabilidad</h2>
            
            <h3>14.1 Limitaciones Generales</h3>
            <p>En la máxima medida permitida por la legislación aplicable:</p>
            <ul>
              <li>El Titular no será responsable de daños indirectos, incidentales, especiales o consecuentes</li>
              <li>Esto incluye, sin limitación: pérdida de beneficios, datos, uso, oportunidades comerciales o reputación</li>
              <li>Incluso si se nos ha informado de la posibilidad de tales daños</li>
            </ul>

            <h3>14.2 Límite Máximo</h3>
            <p>La responsabilidad total del Titular bajo estos Términos no excederá del mayor de:</p>
            <ul>
              <li>El importe que hayas pagado al Titular en los 12 meses anteriores, o</li>
              <li>Cien euros (100 EUR)</li>
            </ul>

            <h3>14.3 Excepciones</h3>
            <p>Nada en estos Términos limita o excluye responsabilidad por:</p>
            <ul>
              <li>Muerte o lesiones personales causadas por negligencia</li>
              <li>Fraude o tergiversación fraudulenta</li>
              <li>Cualquier responsabilidad que no pueda ser excluida por ley</li>
            </ul>

            <h3>14.4 Jurisdicciones de Protección al Consumidor</h3>
            <p>Si eres un consumidor en la Unión Europea o España, las leyes de protección al consumidor pueden otorgarte derechos adicionales que no pueden ser limitados por estos Términos.</p>

            <h2>15. Indemnización</h2>
            <p>Aceptas indemnizar y mantener indemne al Titular, sus empleados, colaboradores y afiliados de cualquier reclamación, daño, pérdida, responsabilidad y gasto (incluyendo honorarios razonables de abogados) derivados de:</p>
            <ul>
              <li>Tu uso del Servicio</li>
              <li>Tu violación de estos Términos</li>
              <li>Tu violación de derechos de terceros</li>
              <li>Tu Contenido del Usuario</li>
            </ul>

            <h2>16. Ley Aplicable y Jurisdicción</h2>
            
            <h3>16.1 Ley Aplicable</h3>
            <p>Estos Términos se rigen e interpretan de acuerdo con la legislación española y, en su caso, el derecho de la Unión Europea.</p>

            <h3>16.2 Jurisdicción</h3>
            <p><strong>Para consumidores (usuarios particulares):</strong></p>
            <p>De conformidad con la legislación de protección de consumidores, si eres un consumidor residente en la Unión Europea o España, puedes presentar reclamaciones ante:</p>
            <ul>
              <li>Los tribunales de tu lugar de residencia, o</li>
              <li>Los tribunales de Las Palmas de Gran Canaria, España</li>
            </ul>

            <p><strong>Para usuarios empresariales:</strong></p>
            <p>Las partes se someten expresamente a la jurisdicción exclusiva de los Juzgados y Tribunales de Las Palmas de Gran Canaria, España.</p>

            <h3>16.3 Resolución Alternativa de Conflictos</h3>
            <p>Si eres un consumidor en la UE, también puedes utilizar la plataforma de resolución de litigios en línea de la Comisión Europea: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a></p>

            <h2>17. Divisibilidad y Renuncia</h2>
            
            <h3>17.1 Divisibilidad</h3>
            <p>Si alguna disposición de estos Términos se considera inválida, ilegal o inaplicable, las disposiciones restantes continuarán en pleno vigor y efecto.</p>

            <h3>17.2 Renuncia</h3>
            <p>El hecho de que no ejerzamos o hagamos cumplir algún derecho o disposición de estos Términos no constituye una renuncia a dicho derecho o disposición.</p>

            <h2>18. Modificaciones de estos Términos</h2>
            <p>Nos reservamos el derecho de modificar estos Términos en cualquier momento. Las modificaciones materiales serán comunicadas mediante:</p>
            <ul>
              <li>Notificación en la Aplicación</li>
              <li>Correo electrónico a la dirección registrada</li>
              <li>Aviso en nuestro Sitio Web</li>
            </ul>
            <p>Los cambios entrarán en vigor inmediatamente tras su publicación o en la fecha especificada en el aviso.</p>
            <p>Tu uso continuado del Servicio después de las modificaciones constituye tu aceptación de los nuevos Términos.</p>
            <p>Si no estás de acuerdo con las modificaciones, debes dejar de utilizar el Servicio.</p>

            <h2>19. Disposiciones Adicionales</h2>
            
            <h3>19.1 Acuerdo Completo</h3>
            <p>Estos Términos, junto con la Política de Privacidad, constituyen el acuerdo completo entre Tú y el Titular en relación con el Servicio.</p>

            <h3>19.2 Cesión</h3>
            <p>No puedes ceder o transferir estos Términos sin nuestro consentimiento previo por escrito. Nosotros podemos ceder estos Términos en cualquier momento.</p>

            <h3>19.3 Notificaciones</h3>
            <p>Las notificaciones bajo estos Términos se realizarán por correo electrónico o mediante aviso en el Servicio.</p>

            <h3>19.4 Idioma</h3>
            <p>Estos Términos se han redactado en español. En caso de traducción a otros idiomas, prevalecerá la versión en español.</p>

            <h2>20. Información de Contacto</h2>
            <p>Si tienes preguntas, comentarios o reclamaciones sobre estos Términos y Condiciones, puedes contactarnos:</p>
            
            <div className="contact-info">
              <p><strong>Responsable:</strong> Facundo Ezequiel Solis</p>
              <p><strong>Dirección:</strong> Calle Albareda, 77, 2D<br />
              Las Palmas de Gran Canaria, España</p>
              <p><strong>Email:</strong> <a href="mailto:soporte@wayoai.app">soporte@wayoai.app</a></p>
              <p><strong>Sitio Web:</strong> <a href="https://wayoai.app" target="_blank" rel="noopener noreferrer">wayoai.app</a></p>
            </div>

            <hr />

            <div className="acceptance-notice">
              <p><strong>AL UTILIZAR EL SERVICIO, RECONOCES QUE HAS LEÍDO, COMPRENDIDO Y ACEPTADO ESTAR SUJETO A ESTOS TÉRMINOS Y CONDICIONES.</strong></p>
            </div>
          </div>
        </div>
      </section>
          <Footer />
    </main>
    </>
  );
}